import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  List,
  Tooltip,
  Paper,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  Save as SaveIcon,
  PhotoLibrary as PhotoLibraryIcon
} from '@mui/icons-material';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { mainApi, remoteApi } from '../../config/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import ImageLibrarySelectionModal from '../accounting/inventory/ImageLibrarySelectionModal';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  fileName: string;
  duration: number;
  order: number;
}

interface MediaSettings {
  mediaItems: MediaItem[];
  transitionType: 'fade' | 'slide' | 'zoom' | 'none';
  transitionDuration: number;
  defaultImageDuration: number;
  orientation: 'landscape' | 'portrait';
  autoPlay: boolean;
}

interface MediaManagementModalProps {
  open: boolean;
  onClose: () => void;
  screenId: number;
  screenCode: string;
  screenName: string;
  currentMediaSettings?: string;
  onSave: () => void;
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    const last = segments[segments.length - 1];
    return decodeURIComponent(last) || 'medya';
  } catch {
    return 'medya';
  }
}

function getMediaTypeFromUrl(url: string): 'image' | 'video' {
  const lower = url.toLowerCase();
  if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov') || lower.includes('video')) {
    return 'video';
  }
  return 'image';
}

const MediaManagementModal: React.FC<MediaManagementModalProps> = ({
  open,
  onClose,
  screenId,
  screenCode,
  screenName,
  currentMediaSettings,
  onSave
}) => {
  const { userDetails } = useAuth();
  
  const [mediaSettings, setMediaSettings] = useState<MediaSettings>({
    mediaItems: [],
    transitionType: 'fade',
    transitionDuration: 500,
    defaultImageDuration: 5000,
    orientation: 'landscape',
    autoPlay: true
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);

  useEffect(() => {
    if (currentMediaSettings) {
      try {
        const parsed = JSON.parse(currentMediaSettings);
        setMediaSettings(parsed);
      } catch (err) {
        console.error('MediaSettings parse hatası:', err);
      }
    }
  }, [currentMediaSettings]);

  const handleLibrarySelect = (selectedUrls: string[]) => {
    const existingUrls = new Set(mediaSettings.mediaItems.map(item => item.url));
    const newItems: MediaItem[] = [];

    for (const url of selectedUrls) {
      if (existingUrls.has(url)) continue;

      const mediaType = getMediaTypeFromUrl(url);
      newItems.push({
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: mediaType,
        url,
        fileName: getFileNameFromUrl(url),
        duration: mediaType === 'image' ? mediaSettings.defaultImageDuration : 0,
        order: mediaSettings.mediaItems.length + newItems.length
      });
    }

    if (newItems.length > 0) {
      setMediaSettings(prev => ({
        ...prev,
        mediaItems: [...prev.mediaItems, ...newItems]
      }));
    }
    setImageLibraryOpen(false);
  };

  const handleDeleteMedia = (id: string) => {
    setMediaSettings(prev => ({
      ...prev,
      mediaItems: prev.mediaItems
        .filter(item => item.id !== id)
        .map((item, index) => ({ ...item, order: index }))
    }));
  };

  const handleMoveUp = (id: string) => {
    const items = [...mediaSettings.mediaItems];
    const index = items.findIndex(item => item.id === id);
    if (index > 0) {
      [items[index], items[index - 1]] = [items[index - 1], items[index]];
      items.forEach((item, idx) => item.order = idx);
      setMediaSettings(prev => ({ ...prev, mediaItems: items }));
    }
  };

  const handleMoveDown = (id: string) => {
    const items = [...mediaSettings.mediaItems];
    const index = items.findIndex(item => item.id === id);
    if (index < items.length - 1) {
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      items.forEach((item, idx) => item.order = idx);
      setMediaSettings(prev => ({ ...prev, mediaItems: items }));
    }
  };

  const handleUpdateDuration = (id: string, duration: number) => {
    setMediaSettings(prev => ({
      ...prev,
      mediaItems: prev.mediaItems.map(item =>
        item.id === id ? { ...item, duration } : item
      )
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log('[MediaModal] Kayit basladi - screenId:', screenId, 'screenCode:', screenCode);

      if (!userDetails?.companyId) {
        setError('Şirket bilgisi bulunamadı.');
        setSaving(false);
        return;
      }

      const requestData = {
        screenId,
        companyId: userDetails.companyId,
        mediaSettings: JSON.stringify(mediaSettings),
        updatedBy: ownAuthSession.getCurrentUid()
      };

      console.log('[MediaModal] update-media POST gonderiliyor:', { screenId, companyId: userDetails.companyId, mediaItemCount: mediaSettings.mediaItems.length });

      const response = await mainApi.post(
        `/api/device/update-media`,
        requestData
      );

      console.log('[MediaModal] update-media response:', response.data);

      if (response.data.success) {
        console.log('[MediaModal] Kayit basarili, TV bildirim gonderiliyor - screenCode:', screenCode);
        try {
          const notifyResponse = await remoteApi.post('/api/tv-management/notify-content-update', {
            screenCode
          });
          console.log('[MediaModal] TV bildirim response:', notifyResponse.data);
        } catch (notifyErr: any) {
          console.warn('[MediaModal] TV bildirim gonderilemedi:', notifyErr?.response?.data || notifyErr?.message || notifyErr);
        }
        onSave();
        onClose();
      } else {
        console.error('[MediaModal] Kayit basarisiz:', response.data);
        setError('Medya ayarları kaydedilemedi.');
      }
    } catch (err: any) {
      console.error('[MediaModal] Kaydetme hatasi:', err?.response?.data || err?.message || err);
      setError(err.response?.data?.message || 'Medya ayarları kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Medya Yönetimi</Typography>
              <Typography variant="caption" color="text.secondary">
                {screenName} ({screenCode})
              </Typography>
            </Box>
            <IconButton edge="end" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Sol: Resim Kütüphanesi ve Ayarlar */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Medya Ekle
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PhotoLibraryIcon />}
                  onClick={() => setImageLibraryOpen(true)}
                  sx={{ mb: 1 }}
                >
                  Resim Kütüphanesinden Seç
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Resim kütüphanesinden resim veya video seçin
                </Typography>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* Geçiş Animasyonu */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Geçiş Animasyonu
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Animasyon Tipi</InputLabel>
                  <Select
                    value={mediaSettings.transitionType}
                    label="Animasyon Tipi"
                    onChange={(e) => setMediaSettings(prev => ({
                      ...prev,
                      transitionType: e.target.value as any
                    }))}
                  >
                    <MenuItem value="fade">Fade (Soldurma)</MenuItem>
                    <MenuItem value="slide">Slide (Kaydırma)</MenuItem>
                    <MenuItem value="zoom">Zoom (Yakınlaşma)</MenuItem>
                    <MenuItem value="none">Yok</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" gutterBottom>
                    Geçiş Süresi: {mediaSettings.transitionDuration}ms
                  </Typography>
                  <Slider
                    value={mediaSettings.transitionDuration}
                    onChange={(_, value) => setMediaSettings(prev => ({
                      ...prev,
                      transitionDuration: value as number
                    }))}
                    min={100}
                    max={2000}
                    step={100}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Paper>

              {/* Varsayılan Resim Süresi */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Varsayılan Resim Süresi
                </Typography>
                <Typography variant="caption" gutterBottom>
                  {(mediaSettings.defaultImageDuration / 1000).toFixed(1)} saniye
                </Typography>
                <Slider
                  value={mediaSettings.defaultImageDuration}
                  onChange={(_, value) => setMediaSettings(prev => ({
                    ...prev,
                    defaultImageDuration: value as number
                  }))}
                  min={1000}
                  max={30000}
                  step={1000}
                  marks={[
                    { value: 1000, label: '1s' },
                    { value: 5000, label: '5s' },
                    { value: 10000, label: '10s' },
                    { value: 30000, label: '30s' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${(value / 1000).toFixed(1)}s`}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Yeni eklenen resimlere bu süre uygulanır
                </Typography>
              </Paper>
            </Grid>

            {/* Sağ: Medya Listesi */}
            <Grid item xs={12} md={8}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {mediaSettings.mediaItems.length === 0 ? (
                <Paper sx={{ p: 8, textAlign: 'center' }}>
                  <ImageIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Henüz medya eklenmemiş
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resim kütüphanesinden medya eklemek için soldaki butonu kullanın
                  </Typography>
                </Paper>
              ) : (
                <List>
                  {mediaSettings.mediaItems
                    .sort((a, b) => a.order - b.order)
                    .map((item, index) => (
                      <Paper key={item.id} sx={{ mb: 2, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={3}>
                            {item.type === 'image' ? (
                              <Card>
                                <CardMedia
                                  component="img"
                                  height="100"
                                  image={item.url}
                                  alt={item.fileName}
                                  sx={{ objectFit: 'cover' }}
                                />
                              </Card>
                            ) : (
                              <Card sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: 'grey.200' }}>
                                <VideoLibraryIcon sx={{ fontSize: 48, color: 'grey.600' }} />
                              </Card>
                            )}
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                  icon={item.type === 'image' ? <ImageIcon /> : <VideoLibraryIcon />}
                                  label={item.type === 'image' ? 'Resim' : 'Video'}
                                  size="small"
                                  color={item.type === 'image' ? 'primary' : 'secondary'}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Sıra: {index + 1}
                                </Typography>
                              </Box>
                              <Typography variant="body2" noWrap>
                                {item.fileName}
                              </Typography>
                              
                              {item.type === 'image' && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption">
                                    Gösterim Süresi: {(item.duration / 1000).toFixed(1)}s
                                  </Typography>
                                  <Slider
                                    value={item.duration}
                                    onChange={(_, value) => handleUpdateDuration(item.id, value as number)}
                                    min={1000}
                                    max={60000}
                                    step={1000}
                                    size="small"
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${(value / 1000).toFixed(1)}s`}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Grid>
                          
                          <Grid item xs={3}>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Yukarı Taşı">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveUp(item.id)}
                                      disabled={index === 0}
                                    >
                                      <ArrowUpwardIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Aşağı Taşı">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveDown(item.id)}
                                      disabled={index === mediaSettings.mediaItems.length - 1}
                                    >
                                      <ArrowDownwardIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Sil">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteMedia(item.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                </List>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <ImageLibrarySelectionModal
        open={imageLibraryOpen}
        onClose={() => setImageLibraryOpen(false)}
        onSave={handleLibrarySelect}
        companyId={userDetails?.companyId || null}
        productName={`${screenName} - Medya`}
        currentImageUrls={mediaSettings.mediaItems.map(item => item.url)}
        productId={null}
      />
    </>
  );
};

export default MediaManagementModal;
